import React from 'react'
import { mkConfig, generateCsv, download } from 'export-to-csv'

import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'

export default function ServerTable({
  columns,
  data,
  initialSorting = [],
  onQueryChange,
  exportFileName = 'export-data',
  pageCount,
  rowCount,
  initialPagination = { pageIndex: 0, pageSize: 40 },
}) {
  const [columnFilters, setColumnFilters] = React.useState([])
  const [pagination, setPagination] = React.useState(initialPagination)
  const [sorting, setSorting] = React.useState(initialSorting)
  const lastSentQueryRef = React.useRef('')

  React.useEffect(() => {
    if (!onQueryChange) return

    const query = {
      columnFilters: columnFilters || [],
      pagination,
      sorting: sorting || [],
    }
    const serializedQuery = JSON.stringify(query)
    if (serializedQuery === lastSentQueryRef.current) return
    lastSentQueryRef.current = serializedQuery

    onQueryChange(query)
  }, [columnFilters, pagination, sorting, onQueryChange])

  const table = useReactTable({
    data: data || [],
    columns,
    manualFiltering: Boolean(onQueryChange),
    manualPagination: Boolean(onQueryChange),
    manualSorting: Boolean(onQueryChange),
    pageCount: typeof pageCount === 'number' ? pageCount : undefined,
    state: {
      columnFilters,
      pagination,
      sorting,
    },
    onColumnFiltersChange: (updater) => {
      setColumnFilters((old) => {
        const nextFilters = typeof updater === 'function' ? updater(old) : updater
        return nextFilters
      })
      setPagination((old) => ({ ...old, pageIndex: 0 }))
    },
    onPaginationChange: setPagination,
    onSortingChange: (updater) => {
      setSorting((old) => {
        const nextSorting = typeof updater === 'function' ? updater(old) : updater
        return nextSorting
      })
      setPagination((old) => ({ ...old, pageIndex: 0 }))
    },
    getCoreRowModel: getCoreRowModel(),
    isMultiSortEvent: () => true,
    initialState: {
      sorting: initialSorting,
      pagination: initialPagination,
    },
  })

  const csvConfig = mkConfig({
    fieldSeparator: ',',
    filename: exportFileName,
    useKeysAsHeaders: true,
  })

  const exportToCsvUtil = (rows) => {
    const rowData = rows.map((row) => row.original)
    const csv = generateCsv(csvConfig)(rowData)
    download(csvConfig)(csv)
  }

  return (
    <>
      <FilterBadges table={table} />
      <TableControlBar
        table={table}
        rowCount={rowCount}
        onExport={() => exportToCsvUtil(table.getRowModel().rows)}
      />
      <div className="table-responsive mt-3 w-100">
        <table className="table table-bordered table-hover mb-0 w-100">
          <thead className="table-light">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <th key={header.id} colSpan={header.colSpan} className="align-middle">
                      {header.isPlaceholder ? null : (
                        <>
                          <div
                            {...{
                              className: header.column.getCanSort()
                                ? 'cursor-pointer select-none'
                                : '',
                              onClick: header.column.getToggleSortingHandler(),
                            }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                            {{
                              asc: ' 🔼',
                              desc: ' 🔽',
                            }[header.column.getIsSorted()] || null}
                          </div>
                          {header.column.getCanFilter() ? (
                            <div className="mt-2">
                              <Filter column={header.column} />
                            </div>
                          ) : null}
                        </>
                      )}
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => {
              return (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <td key={cell.id} className="align-middle">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <TableControlBar
        table={table}
        rowCount={rowCount}
        onExport={() => exportToCsvUtil(table.getRowModel().rows)}
      />
    </>
  )
}

function TableControlBar({ table, onExport, rowCount }) {
  const totalRows = typeof rowCount === 'number'
    ? rowCount
    : table.getPrePaginationRowModel().rows.length

  return (
    <div className="d-flex flex-wrap align-items-center gap-2 mt-3 justify-between">
      <div style={{ marginRight: '20px ' }}>
        {totalRows} Rows
      </div>
      <div style={{ display: 'flex', marginRight: '20px' }}>
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={() => table.setPageIndex(0)}
          disabled={!table.getCanPreviousPage()}
        >
          {'<<'}
        </button>
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
        >
          {'<'}
        </button>
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
        >
          {'>'}
        </button>
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={() => table.setPageIndex(table.getPageCount() - 1)}
          disabled={!table.getCanNextPage()}
        >
          {'>>'}
        </button>
      </div>
      <span className="d-flex align-items-center gap-1 ms-2" style={{ marginRight: '20px' }}>
        <span style={{ marginLeft: '5px' }}>Page</span>
        <span>
          <strong>
            {table.getState().pagination.pageIndex + 1} of{' '}
            {table.getPageCount()}
          </strong>
        </span>
      </span>

      <span className="d-flex align-items-center gap-1 ms-2" style={{ marginRight: '20px' }}>
        Go to page:
        <input
          type="number"
          min="1"
          max={Math.max(table.getPageCount(), 1)}
          value={table.getState().pagination.pageIndex + 1}
          onChange={(e) => {
            const page = e.target.value ? Number(e.target.value) - 1 : 0
            table.setPageIndex(page)
          }}
          className="form-control form-control-sm"
          style={{ marginLeft: '5px', width: '80px', height: '23px', borderRadius: '4px', borderColor: 'rgb(118, 118, 118)' }}
        />
      </span>

      <select
        className="form-select form-select-sm"
        style={{ width: '120px' }}
        value={table.getState().pagination.pageSize}
        onChange={(e) => {
          table.setPageSize(Number(e.target.value))
        }}
      >
        {[40, 80, 120, 160, 200].map((pageSize) => (
          <option key={pageSize} value={pageSize}>
            Show {pageSize}
          </option>
        ))}
      </select>

      <button
        type="button"
        className="btn btn-outline-secondary btn-sm"
        onClick={onExport}
      >
        Export To CSV
      </button>
    </div>
  )
}

function FilterBadges({ table }) {
  const columnFilters = table.getState().columnFilters

  if (columnFilters.length === 0) return null

  return (
    <div className="border bg-white p-3 mt-3">
      <div style={{ padding: '10px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        <strong>Active Filters:</strong>
        {columnFilters.map((filter) => {
          const column = table.getColumn(filter.id)
          const columnHeader = column.columnDef.header

          return (
            <span
              key={filter.id}
              style={{
                border: '1px solid #b3b3b3',
                background: '#e0e0e0',
                padding: '4px 8px',
                borderRadius: '5px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px',
              }}
            >
              {typeof columnHeader === 'string' ? columnHeader : filter.id}: {String(filter.value)}
              <button
                onClick={() => column.setFilterValue(undefined)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  padding: 0,
                  borderRadius: '5px',
                }}
              >
                ×
              </button>
            </span>
          )
        })}
        <button className="btn btn-outline-secondary btn-sm" onClick={() => table.resetColumnFilters()}>Clear All</button>
      </div>
    </div>
  )
}

function Filter({ column }) {
  const columnFilterValue = column.getFilterValue()
  const meta = column.columnDef.meta || {}

  if (meta.filterVariant === 'select') {
    const options = Array.isArray(meta.selectOptions) ? meta.selectOptions : []

    return (
      <select
        className="form-select form-select-sm"
        value={columnFilterValue ?? ''}
        onChange={(e) => column.setFilterValue(e.target.value || undefined)}
      >
        <option value="">All</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    )
  }

  return (
    <DebouncedInput
      className="w-36 border"
      onChange={(value) => column.setFilterValue(value)}
      placeholder="Search..."
      type="text"
      value={columnFilterValue || ''}
    />
  )
}

function DebouncedInput({
  style,
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) {
  const [value, setValue] = React.useState(initialValue)
  const onChangeRef = React.useRef(onChange)

  React.useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  React.useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      onChangeRef.current(value)
    }, debounce)

    return () => clearTimeout(timeout)
  }, [value, debounce])

  return (
    <input
      {...props}
      style={style || { width: '100%', padding: '4px', borderRadius: '4px', height: '23px' }}
      value={value}
      onChange={(e) => setValue(e.target.value)}
    />
  )
}
